import React, { useState, useEffect } from 'react';
import { useParams, Link as ReactRouterLink } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  useColorModeValue,
} from '@chakra-ui/react';
import Breadcrumbs from './shared/Breadcrumbs';
import LoadingSpinner from './shared/LoadingSpinner';
import fetchWithAuth from '../utils/fetchWithAuth';

function FunnelSection({ title, funnel }) {
  if (!funnel) return null;

  return (
    <Box mb={4}>
      <Text fontSize="sm" fontWeight="700" mb={3} textTransform="uppercase" letterSpacing="wide" color="gray.500">
        {title}
      </Text>

      <SimpleGrid columns={{ base: 2, md: 5 }} spacing={3} mb={4}>
        <Box textAlign="center">
          <Text fontSize="2xl" fontWeight="bold">{funnel.sent.toLocaleString()}</Text>
          <Text fontSize="xs" color="gray.500">Sent</Text>
        </Box>
        <Box textAlign="center">
          <Text fontSize="2xl" fontWeight="bold" color={funnel.human_opens > 0 ? 'blue.500' : undefined}>
            {funnel.human_open_rate}%
          </Text>
          <Text fontSize="xs" color="gray.500">Human Open Rate</Text>
          <Text fontSize="xs" color="gray.400">{funnel.human_opens} of {funnel.sent.toLocaleString()}</Text>
        </Box>
        <Box textAlign="center">
          <Text fontSize="2xl" fontWeight="bold" color={funnel.started > 0 ? 'teal.500' : undefined}>
            {funnel.start_rate}%
          </Text>
          <Text fontSize="xs" color="gray.500">Start Rate</Text>
          <Text fontSize="xs" color="gray.400">{funnel.started} of {funnel.human_opens} opens</Text>
        </Box>
        <Box textAlign="center">
          <Text fontSize="2xl" fontWeight="bold" color={funnel.completed > 0 ? 'green.500' : undefined}>
            {funnel.completion_rate_of_starts}%
          </Text>
          <Text fontSize="xs" color="gray.500">Completion / Starts</Text>
          <Text fontSize="xs" color="gray.400">{funnel.completed} of {funnel.started} starts</Text>
        </Box>
        <Box textAlign="center">
          <Text fontSize="2xl" fontWeight="bold" color={funnel.completed > 0 ? 'green.600' : undefined}>
            {funnel.completion_rate_of_opens}%
          </Text>
          <Text fontSize="xs" color="gray.500">Completion / Opens</Text>
          <Text fontSize="xs" color="gray.400">{funnel.completed} of {funnel.human_opens} opens</Text>
        </Box>
      </SimpleGrid>

      {/* Visual funnel bars */}
      <VStack spacing={1} align="stretch" mb={2}>
        <HStack>
          <Text fontSize="xs" w="90px" flexShrink={0} color="gray.500">Sent</Text>
          <Progress value={100} size="xs" flex={1} colorScheme="gray" borderRadius="sm" />
          <Text fontSize="xs" w="40px" textAlign="right" color="gray.500">{funnel.sent.toLocaleString()}</Text>
        </HStack>
        <HStack>
          <Text fontSize="xs" w="90px" flexShrink={0} color="gray.500">Human Opens</Text>
          <Progress
            value={funnel.sent > 0 ? (funnel.human_opens / funnel.sent) * 100 : 0}
            size="xs" flex={1} colorScheme="blue" borderRadius="sm"
          />
          <Text fontSize="xs" w="40px" textAlign="right" color="gray.500">{funnel.human_opens}</Text>
        </HStack>
        <HStack>
          <Text fontSize="xs" w="90px" flexShrink={0} color="gray.500">Started Q1</Text>
          <Progress
            value={funnel.sent > 0 ? (funnel.started / funnel.sent) * 100 : 0}
            size="xs" flex={1} colorScheme="teal" borderRadius="sm"
          />
          <Text fontSize="xs" w="40px" textAlign="right" color="gray.500">{funnel.started}</Text>
        </HStack>
        <HStack>
          <Text fontSize="xs" w="90px" flexShrink={0} color="gray.500">Completed</Text>
          <Progress
            value={funnel.sent > 0 ? (funnel.completed / funnel.sent) * 100 : 0}
            size="xs" flex={1} colorScheme="green" borderRadius="sm"
          />
          <Text fontSize="xs" w="40px" textAlign="right" color="gray.500">{funnel.completed}</Text>
        </HStack>
      </VStack>

      {funnel.bot_opens > 0 && (
        <Text fontSize="xs" color="gray.400">{funnel.bot_opens} bot opens filtered</Text>
      )}
    </Box>
  );
}

function SurveyMonitor() {
  const { pollId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const bgColor = useColorModeValue('background.shade1');
  const boxBgColor = useColorModeValue('background.shade2');
  const borderColor = useColorModeValue('gray.300');

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollId]);

  const fetchData = async () => {
    try {
      const response = await fetchWithAuth(`/api/polls/${pollId}/survey-monitor/`);
      if (response.ok) {
        setData(await response.json());
        setLastUpdated(new Date());
      }
    } catch (err) {
      console.error('Failed to fetch monitor data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box minH="100vh" bg={bgColor} display="flex" alignItems="center" justifyContent="center">
        <LoadingSpinner />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box minH="100vh" bg={bgColor} p={8}>
        <Text>Failed to load survey monitor data.</Text>
      </Box>
    );
  }

  const { sms_funnel, email_funnel, question_funnel, median_completion_seconds, recent_opens, recent_completions } = data;

  // Use human opens from the active funnel for per-question drop-off baseline
  const primaryFunnel = sms_funnel || email_funnel;
  const humanOpensTotal = (sms_funnel?.human_opens || 0) + (email_funnel?.human_opens || 0);

  const formatTime = (seconds) => {
    if (!seconds) return '--';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '--';
    const d = new Date(ts);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' });
  };

  return (
    <Box minH="100vh" bg={bgColor}>
      <Box maxW="900px" mx="auto" px={4} py={8}>
        <Breadcrumbs items={[
          { label: 'Home', path: '/' },
          { label: 'Polls', path: '/pollsPage' },
          { label: data.poll_title, path: `/live-poll/${pollId}` },
          { label: 'Survey Monitor' },
        ]} />

        <HStack justify="space-between" mb={6} align="baseline">
          <Heading as="h1" size="lg">Survey Monitor</Heading>
          <HStack spacing={3}>
            {median_completion_seconds && (
              <Text fontSize="sm" color="gray.500">
                Median time: <strong>{formatTime(median_completion_seconds)}</strong>
              </Text>
            )}
            <Text fontSize="xs" color="gray.400">
              Auto-refreshes every 15s {lastUpdated && `· ${formatTimestamp(lastUpdated)}`}
            </Text>
          </HStack>
        </HStack>

        {/* Channel Funnels */}
        <Box bg={boxBgColor} p={6} borderRadius="md" border="1px solid" borderColor={borderColor} mb={6}>
          <Heading as="h3" size="sm" mb={4}>Engagement Funnel</Heading>
          <FunnelSection title="SMS" funnel={sms_funnel} />
          <FunnelSection title="Email" funnel={email_funnel} />
          {!sms_funnel && !email_funnel && (
            <Text color="gray.500" fontSize="sm">No sends yet.</Text>
          )}
        </Box>

        {/* Per-Question Drop-off */}
        <Box bg={boxBgColor} p={6} borderRadius="md" border="1px solid" borderColor={borderColor} mb={6}>
          <Heading as="h3" size="sm" mb={4}>Per-Question Drop-off</Heading>
          {question_funnel.length === 0 ? (
            <Text color="gray.500" fontSize="sm">No questions found.</Text>
          ) : (
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th w="50px">#</Th>
                  <Th>Question</Th>
                  <Th isNumeric w="80px">Answered</Th>
                  <Th isNumeric w="80px">Drop-off</Th>
                </Tr>
              </Thead>
              <Tbody>
                {question_funnel.map((q, i) => {
                  const prev = i === 0 ? humanOpensTotal : question_funnel[i - 1].responded;
                  const dropoff = prev > 0 ? prev - q.responded : 0;
                  return (
                    <Tr key={q.question_id}>
                      <Td fontWeight="600">Q{q.order}</Td>
                      <Td>
                        <Text fontSize="sm" noOfLines={1}>{q.text}</Text>
                        <Badge fontSize="xs" colorScheme="gray" ml={1}>{q.question_type}</Badge>
                      </Td>
                      <Td isNumeric>{q.responded}</Td>
                      <Td isNumeric>
                        {dropoff > 0 ? (
                          <Text color="red.400" fontSize="sm">-{dropoff}</Text>
                        ) : (
                          <Text color="gray.400" fontSize="sm">0</Text>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          )}
        </Box>

        {/* Recent Activity */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
          <Box bg={boxBgColor} p={5} borderRadius="md" border="1px solid" borderColor={borderColor}>
            <Heading as="h3" size="sm" mb={3}>Recent Opens</Heading>
            {recent_opens.length === 0 ? (
              <Text color="gray.500" fontSize="sm">No opens yet.</Text>
            ) : (
              <VStack spacing={1} align="stretch">
                {recent_opens.map((r) => (
                  <HStack key={r.respondent_id} justify="space-between" fontSize="sm">
                    <HStack>
                      <Badge colorScheme="blue" fontSize="xs">T{r.tranche}</Badge>
                      <Text color="gray.500">#{r.respondent_id}</Text>
                    </HStack>
                    <Text color="gray.400">{formatTimestamp(r.opened_at)}</Text>
                  </HStack>
                ))}
              </VStack>
            )}
          </Box>

          <Box bg={boxBgColor} p={5} borderRadius="md" border="1px solid" borderColor={borderColor}>
            <Heading as="h3" size="sm" mb={3}>Recent Completions</Heading>
            {recent_completions.length === 0 ? (
              <Text color="gray.500" fontSize="sm">No completions yet.</Text>
            ) : (
              <VStack spacing={1} align="stretch">
                {recent_completions.map((r) => (
                  <HStack key={r.respondent_id} justify="space-between" fontSize="sm">
                    <HStack>
                      <Badge colorScheme="green" fontSize="xs">T{r.tranche}</Badge>
                      <Text color="gray.500">#{r.respondent_id}</Text>
                    </HStack>
                    <Text color="gray.400">{formatTimestamp(r.completed_at)}</Text>
                  </HStack>
                ))}
              </VStack>
            )}
          </Box>
        </SimpleGrid>

        <HStack justify="center">
          <Button
            as={ReactRouterLink}
            to={`/live-poll/${pollId}`}
            variant="outline"
            size="sm"
          >
            Back to Dashboard
          </Button>
        </HStack>
      </Box>
    </Box>
  );
}

export default SurveyMonitor;
